/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React, { useState, useRef, useEffect } from 'react';
import makeStyles from '@material-ui/styles/makeStyles';
import { compose } from 'recompose';
import { bindActionCreators } from 'redux';
import { trackEvent as track } from '../../../../../../../state/ducks/TagManager/ducks/TagManager/TagManager-Actions';

import CardWithButtons from '../Common/CardWithButtons';
import { Button, CircularProgress} from '@material-ui/core';
import mbpUtil from 'mbp-api-util';
import Reaptcha from 'reaptcha';
import { bool, func, object, string } from 'prop-types';
import { getAccessTokenSafely } from '../../../../../../../state/ducks/Member/ducks/Auth/Plugin/auth/auth';

import { connect, useSelector } from 'react-redux';
import { getBrand, getFeatureFlags } from '../../../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { getProfileInfo } from '../../../../state/Selectors';
import Disclaimer from '../Common/Disclaimer';
import CardWithButtonsMultiple from '../Common/CardWithButtonsMultiple';
import composePersonalizedContent from '../../../../../../../apis/chatgpt-apis/composePersonalizedContent';
import { useMutation } from 'react-query';

const useStyles = (mobile) =>
    makeStyles((theme) => ({
        contentContainer: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexDirection: 'column',
            zIndex: '2',
            height: '100%',
            width: '100%',
            maxWidth: '715px',
            color: `${theme.palette.common.black} !important`,
            '& > form': {
                width: '100%',
                textAlign: 'center',
            },
            padding: '32px 0 12px 0',
        },
        floraVerseTitleContainer: {
            width: '100%',
            margin: '24px 0',
            padding: '15px 20px',
            boxShadow: 'none',
            borderRadius: '8px',
            backgroundColor: '#fff',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            '& h1': {
                textTransform: 'unset !important',
                fontSize: `${mobile ? '40px' : '72px'} !important`,
                lineHeight: ` ${mobile ? '60px' : '94px'}!important`,
                fontWeight: '800 !important',
                fontFamily: 'Dosis !important',
                marginBottom: `'16px' !important`,
            },
        },
        subtitle: {
            fontWeight: 400,
            maxWidth: `40ch`,
            fontSize: `${mobile ? '16px' : '20px'} !important`,
            lineHeight: `${mobile ? '24px' : '32px'}`,
            textAlign: 'center !important',
            marginBottom: '0px !important',
        },
        generateButton: {
            width: '100% !important',
            height: '58px',
            border: 'none !important',
            float: `${'none'} !important`,
            borderRadius: `${'8px'} !important`,
            margin: `24px 0 12px 0 !important`,
            lineHeight: '26px',
            textTransform: 'none !important',
            backgroundColor: `${'#117B91'} !important`,

            color: `${theme.palette.common.white}`,
            fontWeight: '700 !important',
            '&:disabled': {
                color: '#9D9D9D !important',
                border: '0 !important',
                backgroundColor: '#D9D9D9 !important',
            },
            '& > span': {
                fontSize: `${mobile ? '16px' : '18px'} !important`,
            },
        },
        error: {
            textAlign: 'center !important',
            fontWeight: '400',
            fontSize: '14px !important',
            lineHeight: '20px',
            color: '#DF3030',
            margin: '0 !important',
        },
        recaptchaContainer: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom:'24px',
        },
    }));

const LandingPage = ({
    mobile,
    trackEvent,
    occasion,
    handlePoemMutationSuccess,
    handlePoemMutationError,
    options,
    selections,
    setSelections,
    error,
    setError,
    ...props
}) => {
    // Floraverse
    const people = ['Mom', 'Dad', 'Wife', 'Friend', 'Coworker'];
    const floraVerseCategoryOptions = ['Just Because Gift', 'Birthday', 'Congratulations', 'Sympathy', 'Love & Romance', 'Thank You'];
    const likes = [
        'Gardening',
        'Wine & Bar',
        'Travel',
        'Tailgating',
        'Spa Treatments',
        'Crafts / DIY',
        'Exercise',
        'Reading',
        'Music',
        'Cooking',
        'Photography',
    ];
    const floraVerseType = ['Gift Message', 'Poem', 'Haiku', 'Funny Gift Message'];

    const APP_GOOGLE_RECAPCHA_KEY = mbpUtil.getEnv('APP_GOOGLE_RECAPCHA_KEY');
    const createPoemMutation = useMutation({
        mutationFn: composePersonalizedContent,
        onSuccess: ({ data: { poem, isSuccessful } }) => handlePoemMutationSuccess(poem, isSuccessful),
        onError: handlePoemMutationError,
    });

    const [captchaVerified, setCaptchaVerified] = useState('');
    const googleCaptcha = useRef(null);
    const featureFlags = useSelector(getFeatureFlags);
    const brand = useSelector(getBrand);
    const profileInfo = useSelector(getProfileInfo);
    const googleCaptchaDisabled = featureFlags['is-poem-page-recaptcha-disabled'];
    const isAddressBookVisible = featureFlags['is-floraverse-addressbook-enabled'];
     const resetCaptcha = async () => {
        await googleCaptcha.current?.reset();
        setCaptchaVerified(false);
    };

    const classesLayout = useStyles(mobile);
    const classes = classesLayout();

    const handleSetCreativeOption = (value) => {
        setSelections((prev) => ({
            ...prev,
            writeA: value.trim(),
        }));
    };

    const handleSetFloraVerseCategory = (value) => {
        setSelections((prev) => ({
            ...prev,
            verseOccasion: value.trim(),
        }));
    };

    const handleSetPeopleOption = (value) => {
        props.setRecipient(value);
        setSelections((prev) => ({
            ...prev,
            forMy: value.trim(),
        }));
    };

    const handleSetLikesMomVerse = (likes) => {
        setSelections((prev) => ({
            ...prev,
            // Who likes should probably be turned into an Array
            whoLikes: {
                optionA: likes?.[0]?.trim() || '',
                optionB: likes?.[1]?.trim() || '',
                optionC: likes?.[2]?.trim() || '',
            },
        }));
    };

    const checkGenerateEnabled = () => {
        const {
            writeA,
            forMy,
            verseOccasion,
            whoLikes: { optionA },
        } = selections;
        const checkCaptchaVerified = googleCaptchaDisabled || captchaVerified;
        return forMy && verseOccasion && optionA && writeA && checkCaptchaVerified && !createPoemMutation.isLoading;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const { forMy, whoLikes, writeA, verseOccasion } = selections;
        const token = await getAccessTokenSafely();
        const captcha = (await googleCaptcha.current?.getResponse()) || '';
        const interests = Object.values(whoLikes).filter((interest) => !!interest);
        const occasion = verseOccasion;
        createPoemMutation.mutate({
            token,
            brand: brand?.description || '1-800-Flowers',
            interests,
            enforceCharacterLimit: true,
            recipient: forMy,
            userId: profileInfo?.email || '',
            verseType: writeA,
            captchaToken: captcha,
            occasion,
        });
        !googleCaptchaDisabled && resetCaptcha();
    };

    useEffect(() => {
        setError('');
    }, [selections, captchaVerified]);

    useEffect(() => {
        if (typeof window !== 'undefined') window.scrollTo(0, 0);
    }, []);

    return (
        <div className={classes.contentContainer}>
            <div className={classes.floraVerseTitleContainer}>
                <h1>
                    Flora Verse
                </h1>
                <p className={classes.subtitle}>
                    Create a personalized message and get unique gift suggestions via our AI-powered guide.
                </p>
            </div>
            <form onSubmit={(e) => handleFormSubmit(e)}>
                <CardWithButtons
                    mobile={mobile}
                    handleOptionChange={handleSetPeopleOption}
                    title="Write a Verse For Your:"
                    options={people}
                    placeholder="Or someone else..."
                    isAddressBookVisible= {isAddressBookVisible}
                    isFloraVerse
                />           
                <CardWithButtons
                    mobile={mobile}
                    handleOptionChange={handleSetFloraVerseCategory}
                    title="The Occasion is:"
                    options={floraVerseCategoryOptions}
                    isFloraVerse
                />
                <CardWithButtonsMultiple
                    mobile={mobile}
                    handleOptionChange={handleSetLikesMomVerse}
                    title="Who Likes:"
                    subtitle="(Select Up To Three)"
                    userSelections={likes}
                    placeholder="Or something else..."
                    isFloraVerse
                />
                <CardWithButtons
                    mobile={mobile}
                    handleOptionChange={handleSetCreativeOption}
                    title="Type of Message:"
                    options={floraVerseType}
                    characterLimitNote= "*Responses will be limited to around 100 characters"
                    isFloraVerse
                />
                {!googleCaptchaDisabled && (
                    <div className={classes.recaptchaContainer}>
                        <Reaptcha
                            ref={googleCaptcha}
                            sitekey={APP_GOOGLE_RECAPCHA_KEY}
                            onVerify={() => setCaptchaVerified(true)}
                            onExpire={() => resetCaptcha()}
                            tabindex={0}
                            size="normal"
                        />
                    </div>
                )}
                <Button
                    onClick={() =>
                        trackEvent({
                            eventCategory: occasion,
                            eventAction: 'CTA Click',
                            eventLabel: 'Click compose now',
                        })
                    }
                    className={classes.generateButton}
                    type="submit"
                    disabled={!checkGenerateEnabled()}
                >
                    {createPoemMutation.isLoading ? <CircularProgress /> : 'Get Suggestions'}
                </Button>
                {error && <p className={classes.error}>{error}</p>}
                <Disclaimer />
            </form>
        </div>
    );
};

LandingPage.defaultProps = {
    mobile: false,
};

LandingPage.propTypes = {
    setRecipient: func.isRequired,
    setOccasionType: func.isRequired,
    mobile: bool.isRequired,
    isDadJokeGPT: bool.isRequired,
    trackEvent: func.isRequired,
    handlePoemMutationSuccess: func.isRequired,
    handlePoemMutationError: func.isRequired,
    options: object.isRequired,
    selections: object.isRequired,
    setSelections: func.isRequired,
    error: string.isRequired,
    setError: func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
    trackEvent: bindActionCreators(track, dispatch),
});

const enhance = compose(connect(null, mapDispatchToProps));

export default enhance(LandingPage);
